import { useEffect, useState } from "react";
import { BookMarked, Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";

const LANGS = [["es", "Español"], ["ar", "العربية"], ["fr", "Français"]];
const EMPTY = { title: "", es: "", ar: "", fr: "" };
export const templateText = (tp) => LANGS.map(([k]) => (tp[k] || "").trim()).filter(Boolean).join("\n\n");

export function useMsgTemplates() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/msg-templates").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return [list, load];
}

// Reusable ES/AR/FR announcement templates for super-admin broadcasts
export function MsgTemplateChips({ list, onPick, onManage }) {
  const { t } = useApp();
  return (
    <div className="flex flex-wrap gap-1.5 items-center" data-testid="tpl-chips">
      {list.map((tp) => (
        <span key={tp.id} className="inline-flex items-center rounded-full border border-[#0B2A5B]/30 bg-[#0B2A5B]/5 text-xs font-semibold overflow-hidden">
          <button type="button" onClick={() => onPick(templateText(tp))} className="px-2.5 h-8 hover:bg-[#0B2A5B]/10" title={templateText(tp)} data-testid={`tpl-pick-${tp.id}`}><BookMarked className="inline w-3 h-3 me-1" />{tp.title}</button>
          {LANGS.filter(([k]) => tp[k]?.trim()).map(([k]) => <button key={k} type="button" onClick={() => onPick(tp[k])} className="px-1.5 h-8 border-s border-[#0B2A5B]/20 uppercase text-[10px] hover:bg-[#0B2A5B]/10" data-testid={`tpl-pick-${tp.id}-${k}`}>{k}</button>)}
        </span>
      ))}
      <button type="button" onClick={onManage} className="px-2.5 h-8 rounded-full border border-dashed border-border text-xs font-semibold hover:bg-secondary flex items-center gap-1" data-testid="tpl-manage"><Plus className="w-3 h-3" />{t("tpl_manage")}</button>
    </div>
  );
}

export function MsgTemplatesDialog({ open, onClose, list, reload }) {
  const { t } = useApp();
  const [form, setForm] = useState(null);
  const save = async () => {
    try {
      if (form.id) await api.put(`/admin/msg-templates/${form.id}`, form); else await api.post("/admin/msg-templates", form);
      toast.success(t("saved")); setForm(null); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { if (!window.confirm(t("confirm"))) return; await api.delete(`/admin/msg-templates/${id}`); reload(); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="tpl-dialog">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><BookMarked className="w-5 h-5" />{t("tpl_title")}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">{t("tpl_desc")}</p>
        {!form ? (
          <div className="space-y-2">
            {!list.length && <p className="text-sm text-muted-foreground" data-testid="tpl-empty">{t("tpl_empty")}</p>}
            {list.map((tp) => (
              <div key={tp.id} className="flex items-start gap-2 rounded-xl border border-border p-3" data-testid={`tpl-row-${tp.id}`}>
                <div className="flex-1 min-w-0"><div className="font-bold text-sm">{tp.title}</div><div className="text-xs text-muted-foreground line-clamp-2 whitespace-pre-line" dir="auto">{templateText(tp)}</div></div>
                <button type="button" onClick={() => setForm({ ...EMPTY, ...tp })} className="h-8 w-8 rounded-lg border border-border grid place-items-center" data-testid={`tpl-edit-${tp.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => del(tp.id)} className="h-8 w-8 rounded-lg hover:bg-red-50 text-[#C7353B] grid place-items-center" data-testid={`tpl-del-${tp.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button type="button" onClick={() => setForm({ ...EMPTY })} className="h-10 px-4 rounded-xl bg-[#F28C28] text-[#1A1A1A] text-sm font-bold flex items-center gap-1" data-testid="tpl-new"><Plus className="w-4 h-4" />{t("tpl_new")}</button>
          </div>
        ) : (
          <div className="space-y-3">
            <label className="block space-y-1"><span className="text-sm font-bold">{t("tpl_name")}</span><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value.slice(0, 80) })} className="w-full h-11 rounded-xl border border-border px-3 text-sm" data-testid="tpl-form-title" /></label>
            {LANGS.map(([k, label]) => (
              <label key={k} className="block space-y-1"><span className="text-sm font-bold">{label}</span>
                <textarea value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value.slice(0, 2000) })} rows={3} dir={k === "ar" ? "rtl" : "ltr"} className="w-full rounded-xl border border-border p-3 text-sm" data-testid={`tpl-form-${k}`} /></label>
            ))}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setForm(null)} className="h-10 px-4 rounded-xl border border-border text-sm font-bold" data-testid="tpl-form-cancel">{t("cancel")}</button>
              <button type="button" disabled={!form.title.trim() || !(form.es.trim() || form.ar.trim() || form.fr.trim())} onClick={save} className="h-10 px-4 rounded-xl bg-[#1F4E8C] text-white text-sm font-bold disabled:opacity-50" data-testid="tpl-form-save">{t("save")}</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
